import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Play, Square, Clock, Plus, Gamepad2, LogOut, Bell, History, ShoppingCart, Coffee, AlertTriangle, CreditCard, LayoutDashboard, ArrowLeft, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import StartRentalDialog from "./StartRentalDialog";
import ActiveRentalCard from "./ActiveRentalCard";
import RentalHistory from "./RentalHistory";
import StandaloneFnBOrder from "./StandaloneFnBOrder";
import StartShiftDialog from "./StartShiftDialog";
import CloseShiftDialog from "./CloseShiftDialog";
import PrepaidRentalDialog from "./PrepaidRentalDialog";
import PSShutdownButton from "./PSShutdownButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function KasirDashboard() {
  const [activeRentals, setActiveRentals] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null); // Unit yang dipilih dari card
  const [selectedPrepaidUnit, setSelectedPrepaidUnit] = useState(null); // Unit untuk prepaid
  const [showPrepaidDialog, setShowPrepaidDialog] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeMainTab, setActiveMainTab] = useState('units'); // 'units', 'fnb', or 'history'
  const [branchFilter, setBranchFilter] = useState('all'); // Filter cabang untuk Unit PS
  const navigate = useNavigate();
  
  // Shift management states
  const [currentShift, setCurrentShift] = useState(null);
  const [showStartShiftDialog, setShowStartShiftDialog] = useState(false);
  const [showCloseShiftDialog, setShowCloseShiftDialog] = useState(false);
  const [showLogoutConfirmDialog, setShowLogoutConfirmDialog] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check for active shift on load
  useEffect(() => {
    checkActiveShift();
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBusinessProfile(response.data.profile);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const checkActiveShift = async () => {
    try {
      // Admin & Supervisor tidak perlu shift management
      if (user.role === 'admin' || user.role === 'supervisor') {
        setCurrentShift(null);
        setShowStartShiftDialog(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/shifts/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.shift && response.data.shift.status === 'active') {
        // Has active shift
        setCurrentShift(response.data.shift);
        setShowStartShiftDialog(false); // Don't show dialog
      } else {
        // No active shift, show start shift dialog (only for kasir)
        setCurrentShift(null);
        setShowStartShiftDialog(true);
      }
    } catch (error) {
      console.error('Error checking shift:', error);
      // FORCE CLEAR STATE on error
      setCurrentShift(null);
      // If error, still show start shift dialog (only for kasir)
      if (user.role === 'kasir') {
        setShowStartShiftDialog(true);
      }
    }
  };

  const handleLogout = () => {
    // Check if there's an active shift
    if (currentShift && currentShift.status === 'active') {
      // Show modern confirmation dialog
      setShowLogoutConfirmDialog(true);
    } else {
      // No active shift, proceed with logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const handleOpenCloseShiftDialog = async () => {
    // Show loading toast
    const loadingToast = toast.loading('Memuat data terbaru...');
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch fresh shift data dengan statistik terbaru
      const shiftRes = await axios.get(`${API}/shifts/active`, { headers });
      
      if (shiftRes.data && shiftRes.data.shift) {
        // Update currentShift dengan data fresh dari backend
        setCurrentShift(shiftRes.data.shift);
        console.log('[CloseShift] Fresh shift data loaded:', shiftRes.data.shift);
      }
      
      // Refresh rentals data juga
      await fetchData(false);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Baru buka modal dengan data fresh
      setShowCloseShiftDialog(true);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error fetching shift data:', error);
      
      // Jika error, tetap buka modal dengan data yang ada (fallback)
      setShowCloseShiftDialog(true);
    }
  };

  const handleConfirmCloseShift = async () => {
    setShowLogoutConfirmDialog(false);
    await handleOpenCloseShiftDialog();
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirmDialog(false);
  };

  const handleShiftStarted = (shift) => {
    setCurrentShift(shift);
    setShowStartShiftDialog(false);
    toast.success('Shift dimulai! Selamat bekerja ');
  };

  const handleShiftClosed = async (shift) => {
    setCurrentShift(null);
    setShowCloseShiftDialog(false);
    toast.success('Shift ditutup! Terima kasih atas kerja kerasnya ');
    
    // Refresh all data immediately to show updated state
    await fetchData(false);
    
    // For kasir: Auto logout after closing shift
    // For admin/supervisor: Stay logged in (they can close other kasir's shifts)
    if (user.role === 'kasir') {
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }, 1500);
    }
  };

  const fetchData = async (isInitialLoad = false) => {
    try {
      // Only show loading indicator on initial load, not on background refresh
      if (isInitialLoad) {
        setLoading(true);
      }
      
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // These requests are independent; run them together to reduce dashboard wait time.
      const [rentalsRes, plugsRes] = await Promise.all([
        axios.get(`${API}/rentals/active`, { headers }),
        axios.get(`${API}/plugs`, { headers })
      ]);
      setActiveRentals(rentalsRes.data.rentals || []);
      const allPlugs = plugsRes.data || [];
      
      // Filter available units (not currently rented)
      const rentedUnitIds = rentalsRes.data.rentals.map(r => r.ps_unit_id);
      const available = allPlugs.filter(p => !rentedUnitIds.includes(p.id));
      setAvailableUnits(available);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      
      // Check if it's an authentication error (401 or 403)
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Silakan login kembali.");
        handleLogout();
        return;
      }
      
      // Only show error toast on initial load to avoid spam
      if (isInitialLoad) {
        toast.error("Gagal memuat data");
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Initial load with loading indicator
    fetchData(true);
    
    // Silent background refresh every 3 seconds (faster for real-time monitoring)
    const interval = setInterval(() => fetchData(false), 3000);
    
    // Page Visibility API - refresh when tab becomes active again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab is now visible/active - force refresh
        console.log('Tab active again - refreshing data...');
        fetchData(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleRentalStarted = () => {
    setShowStartDialog(false);
    setSelectedUnit(null); // Reset selected unit
    fetchData(false);
    // Toast already shown in StartRentalDialog
  };

  const handleStartRentalClick = (unit) => {
    setSelectedUnit(unit); // Set unit yang dipilih
    setShowStartDialog(true);
  };

  const handleRentalStopped = () => {
    fetchData(false);
    // Toast already shown by ActiveRentalCard with total price
  };

  const handleRentalExtended = async () => {
    await fetchData(false);
    // Note: Toast is shown by individual dialogs (Extend or AddOrder)
    // Don't show duplicate toast here
  };

  return (
    <div className="min-h-screen billing-shell billing-redesign-v1">
      {/* Top Header Bar - STICKY - Mobile Responsive */}
      <div className="sticky top-0 z-40 billing-topbar">
        <div className="container mx-auto max-w-7xl px-2 sm:px-4 py-2 sm:py-4">
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <img src="/logo-rimuru.png" alt="Rimuru" className="h-10 w-auto" />
                <div>
                  <h2 className="text-white font-bold text-sm">Rimuru Game Suite</h2>
                  <p className="text-blue-200 text-xs">{user.full_name || 'Kasir'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Supervisor/Admin: Switch to Admin Dashboard */}
                {(user.role === 'supervisor' || user.role === 'admin') && (
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10 text-xs px-2 flex items-center gap-1"
                    title="Kembali ke Dashboard Admin"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    <span className="hidden sm:inline">Admin</span>
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 text-xs px-2 bg-blue-600/30"
                  title="Profile"
                >
                  <User className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 text-xs px-2"
                >
                  <LogOut className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {businessProfile && (
              <div className="billing-business-profile flex items-center gap-2 px-2 py-1.5 rounded-xl">
                {businessProfile.logo_url && (
                  <img 
                    src={businessProfile.logo_url} 
                    alt="Logo" 
                    className="h-8 w-8 object-contain rounded bg-white/10 p-0.5"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-xs truncate">{businessProfile.business_name}</p>
                  <p className="text-xs text-blue-200 truncate">{user.branch || 'Semua Cabang'}</p>
                </div>
                {currentShift && currentShift.status === 'active' && user.role !== 'admin' && (
                  <Button
                    onClick={handleOpenCloseShiftDialog}
                    variant="outline"
                    size="sm"
                    className="border-orange-400/50 text-orange-300 hover:bg-orange-500/20 text-xs px-2"
                  >
                    <Clock className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex justify-between items-center">
            {/* Left: Software Branding */}
            <div className="flex items-center gap-4">
              <img src="/logo-rimuru.png" alt="Rimuru Game Suite" className="h-16 w-auto" />
              <div className="border-l border-white/20 pl-4">
                <h2 className="text-white font-bold text-lg">Rimuru Game Suite</h2>
                <p className="text-blue-200 text-sm">{user.full_name || 'Kasir'}</p>
              </div>
            </div>

            {/* Right: Business Profile + Actions */}
            <div className="flex items-center gap-4">
              {/* Business Profile Display */}
              {businessProfile && (
                <div className="billing-business-profile flex items-center gap-3 px-4 py-2 rounded-xl">
                  {businessProfile.logo_url && (
                    <img 
                      src={businessProfile.logo_url} 
                      alt="Business Logo" 
                      className="h-10 w-10 object-contain rounded bg-white/10 p-1"
                    />
                  )}
                  <div className="text-right">
                    <p className="font-bold text-white text-sm">{businessProfile.business_name}</p>
                    <p className="text-xs text-blue-200">{user.branch || 'Semua Cabang'}</p>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex items-center gap-2">
              {currentShift && currentShift.status === 'active' && user.role !== 'admin' && (
                <Button
                  onClick={handleOpenCloseShiftDialog}
                  variant="outline"
                  size="sm"
                  className="border-orange-400/50 text-orange-300 hover:bg-orange-500/20 hover:border-orange-400"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Tutup Shift
                </Button>
              )}
              {(user.role === 'supervisor' || user.role === 'admin') && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Dashboard Admin
                </Button>
              )}
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 bg-blue-600/30 flex items-center gap-2"
                title="Profile"
              >
                <User className="h-4 w-4" />
                Profile
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto max-w-[1480px] py-4 sm:py-7 px-3 sm:px-6 billing-main">
        {/* Header - Compact for Mobile */}
        <div className="mb-3 sm:mb-6 lg:mb-8">
          <div className="lg:flex items-center justify-between">
            <div className="mb-2 sm:mb-4 lg:mb-0">
              <div className="flex items-center gap-3">
                <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">Dashboard Kasir</h1>
                {/* Supervisor Mode Indicator */}
                {user.role === 'supervisor' && (
                  <span className="px-3 py-1 bg-orange-500/20 border border-orange-400/50 rounded-full text-orange-300 text-xs sm:text-sm font-bold">
                     Mode: Supervisor
                  </span>
                )}
              </div>
              <p className="text-blue-200 text-xs sm:text-sm lg:text-base hidden sm:block">
                {user.role === 'supervisor' ? 'Akses Supervisor - Transaksi tanpa shift' : 'Kelola rental PlayStation'}
              </p>
            </div>
            
            {/* Welcome Message */}
            <div className="hidden lg:block text-right">
              <p className="text-white/60 text-sm">Selamat Datang,</p>
              <h2 className="text-white text-2xl font-bold">
                {user.full_name || user.username || 'Kasir'} 
              </h2>
            </div>
          </div>
        </div>

        {/* Date Time Display - Compact for Mobile */}
        <div className="mb-4 sm:mb-6">
          {/* Desktop Version */}
          <div className="hidden lg:block">
            <div className="billing-time-hero rounded-2xl p-6 relative overflow-hidden">
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Clock className="h-10 w-10 text-white" />
                  </div>
                  <div>
                    <div className="text-white/80 text-sm font-bold uppercase tracking-wider mb-1">
                       RIMURU GAME SUITE
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-black text-white drop-shadow-lg" style={{ fontFamily: 'monospace' }}>
                        {currentTime.toLocaleDateString('id-ID', { weekday: 'long' })}
                      </span>
                      <span className="text-2xl font-bold text-purple-200">
                        {currentTime.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-white/80 text-sm font-bold uppercase tracking-wider mb-1">
                    WAKTU OPERASIONAL
                  </div>
                  <div className="bg-slate-900/50 px-6 py-3 rounded-xl border-2 border-purple-400/30">
                    <div className="text-5xl font-black text-white drop-shadow-lg" style={{ fontFamily: 'monospace' }}>
                      {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-2 right-2 w-4 h-4 bg-purple-400 opacity-50"></div>
              <div className="absolute bottom-2 left-2 w-4 h-4 bg-blue-400 opacity-50"></div>
            </div>
          </div>

          {/* Mobile Compact Version */}
          <div className="lg:hidden">
            <div className="billing-time-hero rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-white" />
                  <div>
                    <div className="text-white text-xs font-bold">
                      {currentTime.toLocaleDateString('id-ID', { weekday: 'short' })}, {currentTime.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' })}
                    </div>
                  </div>
                </div>
                <div className="bg-slate-900/50 px-3 py-1 rounded-lg border border-purple-400/30">
                  <div className="text-xl font-bold text-white" style={{ fontFamily: 'monospace' }}>
                    {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards - Compact for Mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
          <Card className="billing-stat-card billing-stat-card--active rounded-2xl">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-1 sm:mb-2">Active Rentals</p>
                  <div className="text-3xl sm:text-5xl font-bold text-white">{activeRentals.length}</div>
                  <p className="text-xs sm:text-sm text-blue-100 mt-1 sm:mt-2"> PS units sedang disewa</p>
                </div>
                <div className="p-3 sm:p-4 bg-white/20 rounded-full">
                  <Gamepad2 className="h-6 w-6 sm:h-10 sm:w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="billing-stat-card billing-stat-card--warning rounded-2xl">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-1 sm:mb-2">Expiring Soon</p>
                  <div className="text-3xl sm:text-5xl font-bold text-white">
                    {activeRentals.filter(r => r.is_expiring_soon).length}
                  </div>
                  <p className="text-xs sm:text-sm text-orange-100 mt-1 sm:mt-2"> Kurang dari 10 menit</p>
                </div>
                <div className="p-3 sm:p-4 bg-white/20 rounded-full">
                  <Clock className="h-6 w-6 sm:h-10 sm:w-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sticky workspace navigation: no more scrolling to reach history. */}
        <div className="mb-4 sm:mb-8">
          <div className="billing-workspace-nav sticky top-[72px] lg:top-[97px] z-30 flex items-center justify-between mb-3 sm:mb-4 py-2">
            <div className="billing-segmented flex gap-1.5 sm:gap-2 w-full sm:w-auto overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveMainTab('units')}
                className={`px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-bold text-xs sm:text-base transition-all flex items-center gap-1 sm:gap-2 ${
                  activeMainTab === 'units'
                    ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                <Gamepad2 className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Unit PS ({
                  (branchFilter === 'all' 
                    ? availableUnits.length + activeRentals.length 
                    : availableUnits.filter(u => u.branch === branchFilter).length + 
                      activeRentals.filter(r => r.branch === branchFilter).length)
                })</span>
                <span className="sm:hidden">PS ({
                  (branchFilter === 'all' 
                    ? availableUnits.length + activeRentals.length 
                    : availableUnits.filter(u => u.branch === branchFilter).length + 
                      activeRentals.filter(r => r.branch === branchFilter).length)
                })</span>
              </button>
              <button
                onClick={() => setActiveMainTab('fnb')}
                className={`px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-bold text-xs sm:text-base transition-all flex items-center gap-1 sm:gap-2 ${
                  activeMainTab === 'fnb'
                    ? 'bg-gradient-to-r from-orange-600 to-pink-600 text-white shadow-lg scale-105'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                <Coffee className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">F&B Only</span>
                <span className="sm:hidden">F&B</span>
              </button>
              <button
                onClick={() => setActiveMainTab('history')}
                className={`px-3 py-2 sm:px-6 sm:py-3 rounded-lg font-bold text-xs sm:text-base transition-all flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
                  activeMainTab === 'history'
                    ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white shadow-lg'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                <History className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Riwayat & Pembayaran</span>
                <span className="sm:hidden">Riwayat</span>
              </button>
            </div>
          </div>
          
          {/* Content based on active tab */}
          {activeMainTab === 'units' ? (
            <>
              {/* Branch Filter for Units */}
              {(availableUnits.length > 0 || activeRentals.length > 0) && (() => {
                // Get unique branches from all units and rentals
                const allUnits = [...activeRentals.map(r => ({ branch: r.branch })), ...availableUnits];
                const uniqueBranches = [...new Set(allUnits.map(u => u.branch).filter(Boolean))];
                
                // Always show filter if there are branches
                return uniqueBranches.length > 0 ? (
                  <div className="mb-4">
                    <select
                      value={branchFilter}
                      onChange={(e) => setBranchFilter(e.target.value)}
                      className="w-full sm:w-auto px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all" className="bg-slate-800"> Semua Cabang</option>
                      {uniqueBranches.map(branch => (
                        <option key={branch} value={branch} className="bg-slate-800">{branch}</option>
                      ))}
                    </select>
                  </div>
                ) : null;
              })()}
              
              {(availableUnits.length > 0 || activeRentals.length > 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 billing-unit-grid">
              {/* Render Active Rentals first */}
              {activeRentals.filter(rental => branchFilter === 'all' || rental.branch === branchFilter).map((rental, index) => (
                <ActiveRentalCard
                  key={rental.id}
                  rental={rental}
                  onStopped={handleRentalStopped}
                  onExtended={handleRentalExtended}
                  index={index}
                />
              ))}
              
              {/* Then render Available Units with smooth animations */}
              {availableUnits.filter(unit => branchFilter === 'all' || unit.branch === branchFilter).map((unit, index) => (
                <Card 
                  key={unit.id} 
                  className={`billing-unit-card backdrop-blur-sm transition-all duration-300 ease-out ${
                    unit.connection_status === 'unplugged'
                      ? 'bg-gradient-to-br from-gray-500/20 to-gray-600/20 border-gray-400/30 hover:border-gray-400/60 hover:shadow-gray-500/30 opacity-75'
                        : 'bg-gradient-to-br from-green-500/20 to-blue-500/20 border-green-400/30 hover:border-green-400/60 hover:shadow-green-500/30'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="text-center space-y-3">
                      {/* Icon dengan animasi */}
                      <div className="flex justify-center mb-2">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/30 rounded-full">
                          <Gamepad2 className="h-10 w-10 text-green-400" />
                        </div>
                      </div>
                      
                      {/* Badge PS Type - DI BAWAH ICON (FULL WIDTH CENTER) */}
                      {unit.ps_type && (
                        <div className="flex justify-center mb-2">
                          <span className="px-4 py-1 bg-blue-500 rounded-full shadow-md text-sm font-bold text-white">
                             {unit.ps_type}
                          </span>
                        </div>
                      )}
                      
                      {/* Nama PS - BESAR */}
                      <h3 className="text-3xl font-bold text-white drop-shadow-lg">
                        {unit.name}
                      </h3>
                      
                      {/* Status Badge - Check connection status only */}
                      {unit.connection_status === 'unplugged' ? (
                        <div className="flex items-center justify-center gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                          <span className="text-xs text-gray-400 font-semibold"> TIDAK TERCOLOK</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <span className="w-2 h-2 bg-green-400 rounded-full animate-ping"></span>
                          <span className="text-xs text-green-300 font-semibold">SIAP DIGUNAKAN</span>
                        </div>
                      )}
                      
                      {/* Cabang */}
                      <p className="text-sm text-blue-200">
                         {unit.branch || unit.branch_name || user.branch || 'Cabang Utama'}
                      </p>
                      
                      {/* Tarif - SELALU TAMPIL */}
                      <div className="bg-yellow-500/20 border border-yellow-400/30 rounded-lg p-3">
                        <p className="text-xs text-yellow-200 uppercase tracking-wider">Tarif Per Jam</p>
                        <p className="text-2xl font-bold text-yellow-300 drop-shadow-lg">
                          Rp {(unit.price_per_hour || 0).toLocaleString('id-ID')}
                        </p>
                      </div>
                      
                      {/* Tombol Bayar di Awal & Mulai Rental */}
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card onClick from firing
                            setSelectedPrepaidUnit(unit);
                            setShowPrepaidDialog(true);
                          }}
                          disabled={unit.connection_status === 'unplugged'}
                          className={`font-bold shadow-lg transition-all flex items-center justify-center px-2 py-2 text-xs ${
                            unit.connection_status === 'unplugged'
                              ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white transition-colors'
                          }`}
                        >
                          <CreditCard className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">Bayar di Awal</span>
                        </Button>
                        
                        <Button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card onClick from firing
                            handleStartRentalClick(unit);
                          }}
                          disabled={unit.connection_status === 'unplugged'}
                          className={`font-bold shadow-lg transition-all flex items-center justify-center px-2 py-2 text-xs ${
                            unit.connection_status === 'unplugged'
                              ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                              : 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white transition-colors'
                          }`}
                        >
                          <Play className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">Mulai</span>
                        </Button>
                      </div>
                      
                      {/* PS Shutdown Button - Only shows if needed */}
                      <div className="mt-2">
                        <PSShutdownButton 
                          unitId={unit.id} 
                          onShutdownComplete={() => {
                            // Refresh data after PS shutdown
                            fetchData();
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-12 text-center">
                <Gamepad2 className="h-16 w-16 text-white/30 mx-auto mb-4" />
                <p className="text-xl text-white/60">Tidak ada unit tersedia</p>
                <p className="text-sm text-white/40 mt-2">Semua PS sedang disewa</p>
              </CardContent>
            </Card>
          )}
            </>
          ) : activeMainTab === 'fnb' ? (
            // F&B Only Tab Content
            <div className="min-h-[400px]">
              <StandaloneFnBOrder />
            </div>
          ) : (
            <section className="billing-history-workspace min-h-[520px]">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                    <History className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
                    Riwayat & Pembayaran
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-400 mt-1">Cari transaksi, cek pembayaran, dan cetak ulang bukti transaksi.</p>
                </div>
                <Button
                  onClick={() => setActiveMainTab('units')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 whitespace-nowrap"
                >
                  <ArrowLeft className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Kembali ke Unit</span>
                </Button>
              </div>
              <div className="bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-2xl p-3 sm:p-5">
                <RentalHistory />
              </div>
            </section>
          )}
        </div>
      </main>

      {/* Start Rental Dialog */}
      <StartRentalDialog
        open={showStartDialog}
        onClose={() => {
          setShowStartDialog(false);
          setSelectedUnit(null);
        }}
        availableUnits={availableUnits}
        preSelectedUnit={selectedUnit}
        onSuccess={handleRentalStarted}
      />

      {/* Start Shift Dialog */}
      <StartShiftDialog
        open={showStartShiftDialog}
        onClose={() => setShowStartShiftDialog(false)}
        onShiftStarted={handleShiftStarted}
      />

      {/* Close Shift Dialog */}
      <CloseShiftDialog
        open={showCloseShiftDialog}
        onClose={() => setShowCloseShiftDialog(false)}
        shift={currentShift}
        onShiftClosed={handleShiftClosed}
      />

      {/* Prepaid Rental Dialog */}
      {selectedPrepaidUnit && (
        <PrepaidRentalDialog
          unit={selectedPrepaidUnit}
          isOpen={showPrepaidDialog}
          onClose={() => {
            setShowPrepaidDialog(false);
            setSelectedPrepaidUnit(null);
          }}
          onSuccess={() => {
            fetchData(); // Refresh units and active rentals
          }}
        />
      )}

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutConfirmDialog} onOpenChange={setShowLogoutConfirmDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <AlertDialogTitle className="text-xl">Shift Masih Aktif</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base text-gray-600 pt-2">
              Anda memiliki shift yang masih aktif. Untuk logout, Anda perlu menutup shift terlebih dahulu. Apakah Anda ingin menutup shift sekarang?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={handleCancelLogout} className="bg-gray-100 hover:bg-gray-200">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCloseShift}
              className="bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white"
            >
              Tutup Shift & Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
